import React from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Mail, Phone, Shield, Car, TrendingUp, Heart, LogOut, Bell, Calendar, Pencil } from 'lucide-react';
import BadgeVerification from '../components/BadgeVerification';

const MonProfil = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) { navigate('/connexion'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };

  const roleLabel = {
    acheteur: { label: 'Acheteur', color: '#3b82f6' },
    vendeur_particulier: { label: 'Vendeur Particulier', color: '#F5A623' },
    vendeur_pro: { label: 'Vendeur Professionnel', color: '#8b5cf6' },
    admin: { label: 'Administrateur', color: '#ef4444' }
  };

  const role = roleLabel[user.role] || roleLabel.acheteur;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header avec bannière */}
        <div style={{
          background: user.banniere
            ? `url(${getImageUrl(user.banniere)}) center/cover`
            : '#0A0A0A',
          position: 'relative',
          minHeight: '180px'
        }} className="px-4 relative overflow-hidden">
          {user.banniere && (
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.8))'
            }} />
          )}
          <div className="relative z-10 max-w-3xl mx-auto py-8 flex items-center gap-6">
            <div style={{ background: '#F5A623', border: '3px solid rgba(255,255,255,0.3)' }}
              className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              {user.photo_profil ? (
                <img
                  src={getImageUrl(user.photo_profil)}
                  alt="profil"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-black font-black text-3xl">
                  {user.prenom?.[0]}{user.nom?.[0]}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-white text-2xl font-black">{user.prenom} {user.nom}</h1>
              <span
                style={{ background: role.color + '20', color: role.color, border: `1px solid ${role.color}40` }}
                className="text-xs font-bold px-3 py-1 rounded-full mt-2 inline-block"
              >
                {role.label}
              </span>
            </div>
          </div>
        </div>

      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Infos */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-lg text-gray-900 flex items-center gap-2">
              <span style={{ color: '#F5A623' }}>|</span> Informations personnelles
            </h2>
            <Link
              to="/profil/modifier"
              style={{ color: '#F5A623', borderColor: '#F5A623' }}
              className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full border hover:bg-yellow-50 transition"
            >
              <Pencil size={14} /> Modifier
            </Link>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-gray-600">
              <div style={{ background: '#F5A62315' }} className="w-9 h-9 rounded-xl flex items-center justify-center">
                <Mail size={18} style={{ color: '#F5A623' }} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <p className="font-semibold text-gray-900">{user.email}</p>
              </div>
            </div>

            {user.telephone && (
              <div className="flex items-center gap-3 text-gray-600">
                <div style={{ background: '#F5A62315' }} className="w-9 h-9 rounded-xl flex items-center justify-center">
                  <Phone size={18} style={{ color: '#F5A623' }} />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Téléphone</p>
                  <p className="font-semibold text-gray-900">{user.telephone}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 text-gray-600">
              <div style={{ background: '#F5A62315' }} className="w-9 h-9 rounded-xl flex items-center justify-center">
                <Shield size={18} style={{ color: '#F5A623' }} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Statut du compte</p>
                <p className="font-semibold text-gray-900">
                  {user.niveau_verification > 0 ? '✅ Compte vérifié' : '⏳ Compte non vérifié'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Link
            to="/mes-annonces"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#F5A62315' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Car size={22} style={{ color: '#F5A623' }} />
            </div>
            <div>
              <p className="font-black text-gray-900">Mes annonces</p>
              <p className="text-xs text-gray-400">Gérer vos annonces</p>
            </div>
          </Link>

          <Link
            to="/mes-locations"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#F5A62315' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Calendar size={22} style={{ color: '#F5A623' }} />
            </div>
            <div>
              <p className="font-black text-gray-900">Mes Locations</p>
              <p className="text-xs text-gray-400">Gérer vos véhicules à louer</p>
            </div>
          </Link>

          <Link
            to="/mes-reservations"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#3b82f615' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Calendar size={22} className="text-blue-500" />
            </div>
            <div>
              <p className="font-black text-gray-900">Réservations</p>
              <p className="text-xs text-gray-400">Gérer vos réservations</p>
            </div>
          </Link>

          <Link
            to="/mes-favoris"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#ef444415' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Heart size={22} className="text-red-500" />
            </div>
            <div>
              <p className="font-black text-gray-900">Mes favoris</p>
              <p className="text-xs text-gray-400">Vos véhicules sauvegardés</p>
            </div>
          </Link>
          
          <Link
            to="/alertes"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#F5A62315' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Bell size={22} style={{ color: '#F5A623' }} />
            </div>
            <div>
              <p className="font-black text-gray-900">Mes Alertes</p>
              <p className="text-xs text-gray-400">Gérer vos alertes de recherche</p>
            </div>
          </Link>

          <Link to="/verification"
            style={{ borderColor: '#F5A623', color: '#F5A623' }}
            className="flex items-center gap-2 p-4 rounded-xl border hover:bg-yellow-50 transition">
            <Shield size={20} />
            <div>
              <p className="font-bold text-sm">Vérification compte</p>
              <p className="text-xs text-gray-400">Obtenez votre badge</p>
            </div>
          </Link>
            
          {(user.role === 'vendeur_pro' || user.role === 'vendeur_particulier') && (
            <Link
              to="/analytics"
              className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
            >
              <div style={{ background: '#8b5cf615' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
                <TrendingUp size={22} className="text-purple-500" />
              </div>
              <div>
                <p className="font-black text-gray-900">Analytics</p>
                <p className="text-xs text-gray-400">Statistiques de vos annonces</p>
              </div>
            </Link>
          )}

          {(user.role === 'vendeur_pro' || user.role === 'admin') && (
            <Link
              to="/profil-pro"
              className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
            >
              <div style={{ background: '#F5A62315' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
                <span style={{ color: '#F5A623' }} className="text-xl">🏪</span>
              </div>
              <div>
                <p className="font-black text-gray-900">Ma Vitrine</p>
                <p className="text-xs text-gray-400">Personnaliser mon profil pro</p>
              </div>
            </Link>
          )}

          <Link
            to="/abonnements"
            className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 hover:border-yellow-400 transition flex items-center gap-4"
          >
            <div style={{ background: '#F5A62315' }} className="w-12 h-12 rounded-xl flex items-center justify-center">
              <Shield size={22} style={{ color: '#F5A623' }} />
            </div>
            <div>
              <p className="font-black text-gray-900">Abonnements</p>
              <p className="text-xs text-gray-400">Gérer votre plan</p>
            </div>
          </Link>
        </div>

        {/* Déconnexion */}
        <button
          onClick={handleLogout}
          className="w-full bg-white rounded-2xl shadow-sm p-4 border border-gray-100 hover:border-red-300 transition flex items-center justify-center gap-2 text-red-500 font-bold"
        >
          <LogOut size={18} /> Se déconnecter
        </button>
      </div>
    </div>
  );
};

export default MonProfil;